import { memo, FC } from "react";
import { styled, Tooltip, TooltipProps, SxProps, Theme } from "@mui/material";
import Chip from "@mui/material/Chip";
import type { FilterChip } from "../types";

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#fff",
    color: "#424242",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: 12,
    minWidth: 100,
    maxWidth: 400,
    maxHeight: 320,
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
});

interface FilterChipWithTooltipProps {
  chip: FilterChip;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  /** When false, hides the delete (x) icon — used for display-only chips. */
  showDelete?: boolean;
}

/**
 * Chip wrapper with styled tooltip support for showing filter details.
 * Displays a tooltip on hover if tooltipContent is provided.
 */
export const FilterChipWithTooltip: FC<FilterChipWithTooltipProps> = memo(
  function FilterChipWithTooltip({ chip, onClick, sx, showDelete = true }) {
    const chipElement = (
      <Chip
        id={`filter-chip-${chip.id}`}
        label={chip.label}
        size="small"
        onClick={onClick}
        onDelete={showDelete ? chip.onRemove : undefined}
        sx={sx}
      />
    );

    if (!chip.tooltipContent) {
      return chipElement;
    }

    return (
      <StyledTooltip title={chip.tooltipContent} placement="bottom-start" arrow>
        {chipElement}
      </StyledTooltip>
    );
  }
);
